import type { FunctionalComponent } from 'preact';
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import { useStore } from '../store';
import {
  eventWeekdayKeysFromEvent,
  normalizeDayKey,
  shortDayLabel,
  tableSlotKey,
} from '../lib/tableReservationEligibility';
import {
  statusForTableSlot,
  tableStatusPillText,
  slotLabel,
} from '../lib/tableMapUtils';
import {
  fetchEventSeatsPublic,
  postEventTablesHold,
  deleteEventTablesHold,
  releaseHold,
  calculateFees,
} from '../api/client';
import { ContactForm } from './ContactForm';
import { Payment } from './Payment';
import { Confirmation } from './Confirmation';
import { formatCurrency, formatCurrencyMajor } from '../utils/theme';
import { applyOccurrenceDateSelection } from '../lib/applyOccurrenceSelection';
import { beginModalBodyScrollLock } from '../utils/modalScrollLock';
import type { TimeSlot, TablePricingRule, VenueTable, SelectedTable } from '../types';
import { firstWidgetFeeTierForTableFlow } from '../lib/feeTierDesignation';
import {
  getEventOccurrenceEntries,
  getPurchasableOccurrenceEntries,
  resolveDisplayOccurrenceIso,
  type EventOccurrenceEntry,
} from '../lib/eventOccurrenceDates';
import { attachTablePricingFromApiRow } from '../lib/tablePricingAmount';
import {
  buildSlotOptionsForTables,
  maxPartyFromTables,
  pickSmallestFittingTable,
  shouldOmitTableTimeSlots,
  type SlotNorm,
  type HoldTableLike,
} from '../lib/tableChooseForMe';
import { ChooseForMeModal } from '../components/ChooseForMeModal';
import { EventDateSelectModalWidget } from '../components/EventDateSelectModalWidget';
import { normalizeTablePricingToRecord, tablePriceFromEntry } from '../lib/tablePricingAmount';
import { dedupeTimeSlotsByRange, slotMatchesEventDayKeys } from '../lib/eventDayKeys';
import {
  formatFeePreviewInclusions,
  getFeePreviewInclusionsFromUnknownRow,
  getTablePriceLabelFromListAndRaw,
  getTableRulePriceDisplay,
  lineTotalMajorForTableRule,
} from '../lib/feePreview';
import '../tableLounge.css';

type SlotRow = { day: string; start_time: string; end_time: string; slotPricingSource?: TimeSlot } | null;

function tableListPriceLabel(
  listPriceMajor: number,
  raw: unknown,
  slot: { slotPricingSource?: unknown } | null,
  cur: string,
): string {
  const listStr = formatCurrencyMajor(listPriceMajor, cur);
  const fromRaw = getTablePriceLabelFromListAndRaw(listPriceMajor, raw, cur);
  if (fromRaw !== listStr) return fromRaw;
  if (slot?.slotPricingSource) {
    return getTablePriceLabelFromListAndRaw(listPriceMajor, slot.slotPricingSource, cur);
  }
  return fromRaw;
}

function toStatusMap(obj: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    out[k] = typeof v === 'string' ? v : (v as { status?: string })?.status || 'available';
  }
  return out;
}

function extractTablePricingFromPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return null;
  const o = payload as Record<string, unknown>;
  if (o.table_pricing != null) return o.table_pricing;
  const d1 = o.data;
  if (d1 && typeof d1 === 'object' && !Array.isArray(d1)) {
    const d = d1 as Record<string, unknown>;
    if (d.table_pricing != null) return d.table_pricing;
    if (d.data && typeof d.data === 'object' && d.data) {
      const d2 = d.data as Record<string, unknown>;
      if (d2.table_pricing != null) return d2.table_pricing;
    }
  }
  return null;
}

function tablePricingMapFromPayload(payload: unknown): Record<string, unknown> {
  return normalizeTablePricingToRecord(extractTablePricingFromPayload(payload));
}

function selectionKey(
  tableId: string,
  slot: { day: string; start_time: string; end_time: string } | null | undefined,
) {
  if (!slot) return tableId;
  return `${tableId}-${slot.day}-${slot.start_time}-${slot.end_time}`;
}

function resolvePricingForSlot(
  tablePricing: Record<string, unknown>,
  tableId: string,
  slot: SlotRow,
): unknown {
  const prefix = `${tableId}-`;
  const firstTableKeyedEntry = (): unknown => {
    if (tablePricing[tableId] != null) return tablePricing[tableId];
    for (const [key, val] of Object.entries(tablePricing)) {
      if (key === tableId || key.startsWith(prefix)) return val;
    }
    return undefined;
  };

  if (!slot) {
    // Omit-time / null slot: bare id, then any slotted rule for this table.
    return firstTableKeyedEntry();
  }
  const keys = [tableSlotKey(tableId, slot), selectionKey(tableId, slot)];
  for (const k of keys) {
    const v = tablePricing[k];
    if (v != null) return v;
  }
  for (const [key, val] of Object.entries(tablePricing)) {
    if (!key.startsWith(prefix)) continue;
    if (key.includes(slot.start_time) && key.includes(slot.end_time)) return val;
  }
  return firstTableKeyedEntry();
}

function freeTicketsFromRaw(raw: unknown): number {
  if (!raw || typeof raw !== 'object') return 0;
  const n = (raw as { free_tickets_count?: number }).free_tickets_count;
  return typeof n === 'number' && n >= 0 ? n : 0;
}

function formatOccurrenceShort(iso: string, tz?: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz || undefined });
  } catch {
    return '';
  }
}

function holdToSelectedTable(
  h: HoldTableLike,
  tables: VenueTable[],
  tablePricing: Record<string, unknown>,
  feeTierId: string,
  feeCurrency: string,
  slotAsRow: SlotRow,
): SelectedTable {
  const table = tables.find((t) => t.id === h.table_id);
  const label = table?.label ?? h.table_id;
  const slot = h.time_slot
    ? { day: h.time_slot.day, start_time: h.time_slot.start_time, end_time: h.time_slot.end_time }
    : undefined;
  const raw = slotAsRow
    ? resolvePricingForSlot(tablePricing, h.table_id, slotAsRow)
    : resolvePricingForSlot(tablePricing, h.table_id, null);
  const chairsN = h.chairs || Number(table?.chairs) || 1;
  const price = tablePriceFromEntry(raw, chairsN);
  const pr: TablePricingRule = {
    table_id: h.table_id,
    fee_tier_id: feeTierId,
    price,
    currency: feeCurrency,
    label,
    time_slot: slot ? { day: slot.day, start_time: slot.start_time, end_time: slot.end_time } : null,
    free_tickets_count: freeTicketsFromRaw(raw),
  };
  if (raw) attachTablePricingFromApiRow(pr, raw);
  return {
    tableId: h.table_id,
    label,
    chairs: h.chairs,
    timeSlot: slot,
    pricingRule: pr,
  };
}

export const TableReservationSelection: FunctionalComponent = () => {
  const { state, dispatch } = useStore();
  const {
    event,
    venue,
    venueConfig,
    feeStructures,
    widget,
    selectedTables,
    holdId,
    holdApiSource,
    widgetId,
  } = state;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seatPayload, setSeatPayload] = useState<unknown>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [continuing, setContinuing] = useState(false);
  const [chooseForMeOpen, setChooseForMeOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cfmDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [cfmPreview, setCfmPreview] = useState({ party: 4, slotIndex: 0 });
  const [cfmFeeText, setCfmFeeText] = useState('—');
  const [cfmTotalText, setCfmTotalText] = useState('—');
  const onChooseForMePreview = useCallback((party: number, slotIndex: number) => {
    setCfmPreview({ party, slotIndex });
  }, []);

  const tlPanelRef = useRef<HTMLDivElement>(null);
  const tlCartBtnRef = useRef<HTMLButtonElement>(null);
  const tlCartSectionRef = useRef<HTMLDivElement>(null);
  const prevTableCountForFlyRef = useRef(0);
  const [cartFly, setCartFly] = useState<{
    id: number;
    sx: number;
    sy: number;
    dx: number;
    dy: number;
  } | null>(null);
  const [cartPulse, setCartPulse] = useState(false);

  const inCheckout = state.step === 'contact' || state.step === 'payment';
  const showTableModal =
    inCheckout
    || (state.step === 'tickets' && reservationModalOpen)
    || state.step === 'confirmation';

  const defaultTableFeeTier = useMemo(
    () => firstWidgetFeeTierForTableFlow(feeStructures, !!event?.seated_event),
    [feeStructures, event?.seated_event],
  );
  const feeTierId = defaultTableFeeTier?._id;
  const currency = (defaultTableFeeTier?.currency || feeStructures[0]?.currency || 'USD').toLowerCase();
  const feeCurrency = defaultTableFeeTier?.currency || feeStructures[0]?.currency || 'USD';
  const eventId = event?._id;

  const occurrenceEntries = useMemo(() => (event ? getEventOccurrenceEntries(event as unknown) : []), [event]);
  const purchasableOccurrenceEntries = useMemo(
    () => getPurchasableOccurrenceEntries(occurrenceEntries),
    [occurrenceEntries],
  );

  const selectedOccurrenceIso = state.selectedOccurrenceIso;

  const eventForFilter = useMemo(() => {
    if (!event) return null;
    const iso = resolveDisplayOccurrenceIso(event, selectedOccurrenceIso);
    return {
      ...event,
      event_start_date: iso,
      event_end_date: iso,
    };
  }, [event, selectedOccurrenceIso]);

  const eventDays = useMemo(
    () => (eventForFilter ? eventWeekdayKeysFromEvent(eventForFilter) : []),
    [eventForFilter],
  );

  const omitTableTimeSlots = useMemo(
    () => shouldOmitTableTimeSlots(venueConfig, seatPayload),
    [venueConfig, seatPayload],
  );

  const tables: VenueTable[] = useMemo(() => {
    const fromSeat = (seatPayload as { venue?: { tables?: unknown } })?.venue?.tables;
    const fromEvent = venue?.tables;
    const seatArr = Array.isArray(fromSeat) ? (fromSeat as VenueTable[]) : [];
    const eventArr = Array.isArray(fromEvent) ? fromEvent : [];
    const raw = seatArr.length > 0 ? seatArr : eventArr;
    return raw.map((tb) => {
      const any = tb as { id?: string; table_id?: string };
      return { ...tb, id: any.id ?? any.table_id ?? '' };
    });
  }, [seatPayload, venue]);

  const tableAvailability = useMemo(() => {
    const e = (event as { table_availability?: unknown } | null)?.table_availability;
    const fromEvent = toStatusMap(e);
    const fromSeat = toStatusMap(
      (seatPayload as { table_availability?: unknown; data?: { table_availability?: unknown } })?.table_availability ??
        (seatPayload as { data?: { table_availability?: unknown } })?.data?.table_availability,
    );
    return { ...fromEvent, ...fromSeat };
  }, [seatPayload, event]);

  const tablePricing = useMemo(() => {
    const a = (event as { table_pricing?: unknown } | null)?.table_pricing;
    const fromSeats = tablePricingMapFromPayload(seatPayload);
    return {
      ...normalizeTablePricingToRecord(a),
      ...fromSeats,
    } as Record<string, unknown>;
  }, [event, seatPayload]);

  useEffect(() => {
    if (!eventId || !feeTierId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchEventSeatsPublic(eventId, feeTierId);
        if (!cancelled) setSeatPayload(res);
      } catch (e: unknown) {
        if (!cancelled) {
          setError((e as Error).message || 'Failed to load tables');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, feeTierId]);

  useEffect(() => {
    if (omitTableTimeSlots) return;
    if (!eventDays.length) return;
    if (selectedDay === null || !eventDays.includes(selectedDay)) {
      setSelectedDay(eventDays[0]);
    }
  }, [eventDays, selectedDay, omitTableTimeSlots]);

  useEffect(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, [selectedDay, omitTableTimeSlots, dispatch]);

  const tablesWithSlots = useMemo(() => {
    const sortTables = (rows: Array<{ table: VenueTable; slots: SlotRow[] }>) =>
      rows.sort((a, b) =>
        String(a.table.label || a.table.id).localeCompare(String(b.table.label || b.table.id)),
      );

    // Default Zeus mode: one bookable option per table, time_slot: null.
    if (omitTableTimeSlots) {
      return sortTables(tables.map((table) => ({ table, slots: [null] })));
    }

    if (!selectedDay) return [];
    const out: Array<{ table: VenueTable; slots: SlotRow[] }> = [];
    for (const table of tables) {
      const rawSlots = table.time_slots;
      if (!Array.isArray(rawSlots) || rawSlots.length === 0) {
        out.push({ table, slots: [null] });
        continue;
      }
      const matched = rawSlots.filter((s) =>
        slotMatchesEventDayKeys([selectedDay], {
          day: normalizeDayKey(s?.day),
          start_time: s.start_time,
          end_time: s.end_time,
        }),
      );
      if (matched.length === 0) {
        // Require-times on, but no matching event-day slots → null fallback.
        out.push({ table, slots: [null] });
        continue;
      }
      const forDedupe = matched.map((s) => ({
        day: normalizeDayKey(s.day),
        startTime: s.start_time,
        endTime: s.end_time,
        _ref: s,
      }));
      const deduped = dedupeTimeSlotsByRange(forDedupe, [selectedDay]);
      out.push({
        table,
        slots: deduped.map((d) => {
          const src = d._ref;
          return {
            day: src.day,
            start_time: src.start_time,
            end_time: src.end_time,
            slotPricingSource: src,
          };
        }),
      });
    }
    return sortTables(out);
  }, [tables, selectedDay, omitTableTimeSlots]);

  const chooseForMeSlotOptions = useMemo(
    () => buildSlotOptionsForTables(tablesWithSlots as Array<{ table: unknown; slots: SlotNorm[] }>),
    [tablesWithSlots],
  );
  const maxPartyForDay = useMemo(() => maxPartyFromTables(tablesWithSlots), [tablesWithSlots]);

  const cfmSelectedPreview = useMemo(() => {
    if (!chooseForMeOpen || !feeTierId) return null;
    const opt = omitTableTimeSlots
      ? { key: 'open', slot: null as SlotNorm, label: 'Book for event' }
      : chooseForMeSlotOptions[cfmPreview.slotIndex];
    if (!opt) return null;
    const h = pickSmallestFittingTable(
      tablesWithSlots,
      cfmPreview.party,
      opt.slot,
      tableAvailability,
    );
    if (!h) return null;
    const slotAsRow: SlotRow = opt.slot
      ? {
          day: opt.slot.day,
          start_time: opt.slot.start_time,
          end_time: opt.slot.end_time,
          slotPricingSource: undefined,
        }
      : null;
    return holdToSelectedTable(h, tables, tablePricing, feeTierId, feeCurrency, slotAsRow);
  }, [
    chooseForMeOpen,
    feeTierId,
    cfmPreview,
    chooseForMeSlotOptions,
    omitTableTimeSlots,
    tablesWithSlots,
    tableAvailability,
    tables,
    tablePricing,
    feeCurrency,
  ]);

  const cfmSubtotal = cfmSelectedPreview
    ? lineTotalMajorForTableRule(cfmSelectedPreview.pricingRule)
    : 0;

  const cfmInclusionsText = useMemo(
    () =>
      cfmSelectedPreview?.pricingRule
        ? formatFeePreviewInclusions(cfmSelectedPreview.pricingRule.fee_preview)
        : '',
    [cfmSelectedPreview],
  );

  useEffect(() => {
    if (!chooseForMeOpen) {
      setCfmFeeText('—');
      setCfmTotalText('—');
      return;
    }
    if (cfmSubtotal <= 0) {
      setCfmFeeText('—');
      setCfmTotalText('Free');
      return;
    }
    if (!eventId) {
      setCfmFeeText('—');
      setCfmTotalText(formatCurrencyMajor(cfmSubtotal, currency));
      return;
    }
    const cfmTw = cfmSelectedPreview?.pricingRule?.fee_preview?.total_with_fees;
    if (typeof cfmTw === 'number' && Number.isFinite(cfmTw) && cfmTw >= 0) {
      if (cfmDebounceRef.current) clearTimeout(cfmDebounceRef.current);
      setCfmFeeText('—');
      setCfmTotalText(cfmTw === 0 ? 'Free' : formatCurrency(cfmTw, currency));
      return;
    }
    if (cfmDebounceRef.current) clearTimeout(cfmDebounceRef.current);
    setCfmFeeText('…');
    setCfmTotalText('…');
    cfmDebounceRef.current = setTimeout(() => {
      void calculateFees(Math.max(0, Math.round(cfmSubtotal * 100)), currency, 'tickets', undefined, eventId)
        .then((data) => {
          const f =
            data.totals?.total_fees ??
            data.breakdown?.total_fees ??
            (data as { total_fees?: number }).total_fees ??
            0;
          const total =
            data.totals?.total_amount ??
            data.breakdown?.total_amount ??
            Math.round(cfmSubtotal * 100) + f;
          setCfmFeeText(f <= 0 ? '—' : formatCurrency(f, currency));
          setCfmTotalText(total <= 0 ? 'Free' : formatCurrency(total, currency));
        })
        .catch(() => {
          setCfmFeeText('—');
          setCfmTotalText(
            cfmSubtotal <= 0 ? 'Free' : formatCurrencyMajor(cfmSubtotal, currency),
          );
        });
    }, 250);
    return () => {
      if (cfmDebounceRef.current) clearTimeout(cfmDebounceRef.current);
    };
  }, [cfmSubtotal, chooseForMeOpen, currency, eventId, cfmSelectedPreview]);

  /** Sum in major units; uses `total_with_fees` from fee_preview per line when present. */
  const selectionSubtotal = useMemo(
    () => selectedTables.reduce((s, t) => s + lineTotalMajorForTableRule(t.pricingRule), 0),
    [selectedTables],
  );

  const selectionLinesAllInFromFeePreview = useMemo(
    () =>
      selectedTables.length > 0 &&
      selectedTables.every(
        (t) =>
          t.pricingRule?.fee_preview != null &&
          typeof t.pricingRule.fee_preview.total_with_fees === 'number' &&
          Number.isFinite(t.pricingRule.fee_preview.total_with_fees),
      ),
    [selectedTables],
  );

  useEffect(() => {
    if (selectionSubtotal <= 0) {
      dispatch({ type: 'CLEAR_FEE_CALCULATION' });
      return;
    }
    if (selectionLinesAllInFromFeePreview) {
      dispatch({ type: 'CLEAR_FEE_CALCULATION' });
      dispatch({ type: 'SET_CALCULATING_FEES', calculating: false });
      return;
    }
    dispatch({ type: 'SET_CALCULATING_FEES', calculating: true });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const eid = state.event?._id;
      calculateFees(Math.max(0, Math.round(selectionSubtotal * 100)), currency, 'tickets', undefined, eid)
        .then((data) => dispatch({ type: 'SET_FEE_CALCULATION', feeCalculation: data }))
        .catch(() => dispatch({ type: 'CLEAR_FEE_CALCULATION' }));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selectionSubtotal, selectionLinesAllInFromFeePreview, currency, state.event?._id, dispatch]);

  const fees = useMemo(() => {
    if (!state.feeCalculation) return 0;
    if (state.feeCalculation.totals?.total_fees !== undefined) return state.feeCalculation.totals.total_fees;
    if (state.feeCalculation.breakdown?.total_fees !== undefined) return state.feeCalculation.breakdown.total_fees;
    if (state.feeCalculation.total_fees !== undefined) return state.feeCalculation.total_fees;
    return 0;
  }, [state.feeCalculation]);

  const tax = useMemo(() => {
    if (!state.feeCalculation) return 0;
    return state.feeCalculation.totals?.tax ?? state.feeCalculation.breakdown?.tax ?? 0;
  }, [state.feeCalculation]);

  const grandTotal = useMemo(() => {
    if (selectionLinesAllInFromFeePreview) {
      return selectedTables.reduce(
        (s, t) => s + (t.pricingRule?.fee_preview?.total_with_fees ?? 0),
        0,
      );
    }
    const apiTotal = state.feeCalculation?.totals?.total_amount ?? state.feeCalculation?.breakdown?.total_amount;
    if (apiTotal !== undefined) return apiTotal;
    return Math.max(0, Math.round(selectionSubtotal * 100)) + fees + tax;
  }, [state.feeCalculation, selectionSubtotal, selectionLinesAllInFromFeePreview, selectedTables, fees, tax]);

  const selectedKeys = useMemo(() => {
    const s = new Set<string>();
    for (const t of selectedTables) {
      s.add(t.timeSlot ? tableSlotKey(t.tableId, t.timeSlot) : t.tableId);
    }
    return s;
  }, [selectedTables]);

  const tableCartCount = selectedTables.length;
  const scrollToCartSection = useCallback(() => {
    tlCartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  useLayoutEffect(() => {
    const n = tableCartCount;
    const prev = prevTableCountForFlyRef.current;
    if (n > prev && n > 0 && !inCheckout) {
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const cart = tlCartBtnRef.current;
          const panel = tlPanelRef.current;
          if (cart && panel) {
            const cr = cart.getBoundingClientRect();
            const pr = panel.getBoundingClientRect();
            const endCx = cr.left + cr.width / 2;
            const endCy = cr.top + cr.height / 2;
            const startCx = pr.left + pr.width * 0.5;
            const startCy = pr.top + Math.min(pr.height * 0.7, pr.height * 0.9);
            const r = 6;
            setCartFly({
              id: Date.now(),
              sx: startCx - r,
              sy: startCy - r,
              dx: endCx - startCx,
              dy: endCy - startCy,
            });
            setCartPulse(true);
            window.setTimeout(() => setCartFly(null), 600);
            window.setTimeout(() => setCartPulse(false), 900);
          }
        });
      });
      prevTableCountForFlyRef.current = n;
      return () => cancelAnimationFrame(raf);
    }
    prevTableCountForFlyRef.current = n;
  }, [tableCartCount, inCheckout]);

  const makeToggleHandler = useCallback(
    (table: VenueTable, slot: SlotRow) => {
      const tid = String(table.id);
      if (slot) {
        const st = statusForTableSlot(tid, slot, tableAvailability);
        if (st === 'sold' || st === 'held' || st === 'blocked') return;
        const raw = resolvePricingForSlot(tablePricing, tid, slot);
        const chairs = table.chairs || 1;
        const fromSlot = slot.slotPricingSource
          ? tablePriceFromEntry(slot.slotPricingSource as unknown, chairs)
          : 0;
        const fromMap = tablePriceFromEntry(raw, chairs);
        const amount = fromSlot > 0 ? fromSlot : fromMap;
        if (!feeTierId) return;
        const pr: TablePricingRule = {
          table_id: tid,
          fee_tier_id: feeTierId,
          price: amount,
          currency: feeCurrency,
          label: table.label,
          time_slot: { day: slot.day, start_time: slot.start_time, end_time: slot.end_time },
          free_tickets_count: freeTicketsFromRaw(raw),
        };
        if (raw) attachTablePricingFromApiRow(pr, raw);
        else if (slot.slotPricingSource) attachTablePricingFromApiRow(pr, slot.slotPricingSource);
        const sel: SelectedTable = {
          tableId: tid,
          label: table.label,
          chairs: table.chairs || 1,
          timeSlot: { day: slot.day, start_time: slot.start_time, end_time: slot.end_time },
          pricingRule: pr,
        };
        dispatch({ type: 'TOGGLE_TABLE', table: sel });
        return;
      }
      const st = statusForTableSlot(tid, null, tableAvailability);
      if (st === 'sold' || st === 'held' || st === 'blocked') return;
      const raw = resolvePricingForSlot(tablePricing, tid, null);
      const amount = tablePriceFromEntry(raw, table.chairs || 1);
      if (!feeTierId) return;
      const pr: TablePricingRule = {
        table_id: tid,
        fee_tier_id: feeTierId,
        price: amount,
        currency: feeCurrency,
        label: table.label,
        time_slot: null,
        free_tickets_count: freeTicketsFromRaw(raw),
      };
      if (raw) attachTablePricingFromApiRow(pr, raw);
      const sel: SelectedTable = {
        tableId: tid,
        label: table.label,
        chairs: table.chairs || 1,
        pricingRule: pr,
      };
      dispatch({ type: 'TOGGLE_TABLE', table: sel });
    },
    [tableAvailability, tablePricing, feeTierId, feeCurrency, dispatch],
  );

  const handleCheckoutBack = useCallback(async () => {
    if (
      (state.step === 'contact' || state.step === 'payment') &&
      state.holdId &&
      state.event
    ) {
      if (state.holdApiSource === 'event_tables') {
        try {
          await deleteEventTablesHold(state.event._id, state.holdId);
        } catch {
          /* */
        }
      } else {
        try {
          await releaseHold(state.widgetId, state.holdId);
        } catch {
          /* */
        }
      }
      dispatch({ type: 'CLEAR_HOLD' });
    }
    if (state.step === 'contact') {
      dispatch({ type: 'SET_STEP', step: 'tickets' });
    } else if (state.step === 'payment') {
      dispatch({ type: 'SET_STEP', step: 'contact' });
    }
  }, [state.step, state.holdId, state.event, state.holdApiSource, state.widgetId, dispatch]);

  const resetAfterPurchase = useCallback(() => {
    dispatch({ type: 'RESET_WIDGET_PURCHASE' });
    setReservationModalOpen(false);
  }, [dispatch]);

  const handleContinue = useCallback(
    async (override?: SelectedTable[]) => {
      const toHold = override && override.length > 0 ? override : selectedTables;
      if (!eventId || !feeTierId || toHold.length === 0) return;
      if (override && override.length > 0) {
        dispatch({ type: 'REPLACE_TABLE_SELECTION', tables: override });
      }
      setContinuing(true);
      setError(null);
      try {
        if (holdId && holdApiSource === 'event_tables') {
          try {
            await deleteEventTablesHold(eventId, holdId);
          } catch { /* */ }
          dispatch({ type: 'CLEAR_HOLD' });
        }

        const res = (await postEventTablesHold(eventId, {
          fee_tier_id: feeTierId,
          tables: toHold.map((t) => ({
            table_id: t.tableId,
            chairs: t.chairs,
            time_slot: t.timeSlot
              ? { day: t.timeSlot.day, start_time: t.timeSlot.start_time, end_time: t.timeSlot.end_time }
              : null,
          })),
        })) as {
          hold_id?: string;
          holdId?: string;
          data?: { hold_id?: string; expires_in?: number; expires_at?: string; pricing?: { total?: number } };
          pricing?: { total?: number };
          expires_in?: number;
          expires_at?: string;
        };

        const holdIdRes = res.hold_id ?? res.data?.hold_id ?? res.holdId;
        if (!holdIdRes) {
          setError('Could not reserve tables. Please try again.');
          return;
        }

        let expiresIn = 8 * 60;
        if (typeof res.expires_in === 'number') expiresIn = res.expires_in;
        else if (typeof res.data?.expires_in === 'number') expiresIn = res.data.expires_in;
        const exp = res.expires_at ?? res.data?.expires_at;
        if (exp) {
          const s = (new Date(exp).getTime() - Date.now()) / 1000;
          if (s > 10) expiresIn = Math.floor(s);
        }

        const pricingRaw = res.pricing ?? res.data?.pricing;
        // Hold `pricing.total` is major currency units (same as meeteor-web / checkoutCurrencyService).
        let holdMajor =
          pricingRaw && typeof pricingRaw.total === 'number' ? pricingRaw.total : 0;
        if (!(holdMajor > 0)) {
          // Omit-time holds can miss slotted rule keys — fall back to selected line prices.
          holdMajor = toHold.reduce((sum, t) => {
            const p = t.pricingRule?.price;
            return sum + (typeof p === 'number' && p > 0 ? p : 0);
          }, 0);
        }
        const holdCents = holdMajor > 0 ? Math.round(holdMajor * 100) : null;

        dispatch({
          type: 'SET_HOLD',
          holdId: holdIdRes,
          expiresIn,
          holdApiSource: 'event_tables',
          holdPricingCents: holdCents,
        });
        dispatch({ type: 'SET_STEP', step: 'contact' });
      } catch (e: unknown) {
        setError((e as Error).message || 'Could not hold tables. Please try again.');
      } finally {
        setContinuing(false);
        setChooseForMeOpen(false);
      }
    },
    [eventId, feeTierId, selectedTables, holdId, holdApiSource, dispatch],
  );

  const handleChooseForMeConfirm = useCallback(
    (party: number, slot: SlotNorm) => {
      const h = pickSmallestFittingTable(tablesWithSlots, party, slot, tableAvailability);
      if (!h || !feeTierId) {
        setError(
          omitTableTimeSlots
            ? 'No table is available for that party size. Try another size or pick manually.'
            : 'No table is available for that party size and time. Try another slot or pick manually.',
        );
        return;
      }
      const slotRow: SlotRow = slot
        ? { day: slot.day, start_time: slot.start_time, end_time: slot.end_time, slotPricingSource: undefined }
        : null;
      const sel = holdToSelectedTable(h, tables, tablePricing, feeTierId, feeCurrency, slotRow);
      void handleContinue([sel]);
    },
    [
      tablesWithSlots,
      tableAvailability,
      tables,
      tablePricing,
      feeTierId,
      feeCurrency,
      handleContinue,
      omitTableTimeSlots,
    ],
  );

  const onDateModalConfirm = useCallback(
    async (entry: EventOccurrenceEntry) => {
      setDateModalOpen(false);
      setError(null);
      try {
        await applyOccurrenceDateSelection(dispatch, widgetId, event ?? null, entry, {
          widgetBootstrapEventId: state.widgetBootstrapEventId,
          widgetBootstrapFirstOccurrenceIso: state.widgetBootstrapFirstOccurrenceIso,
        });
      } catch (e: unknown) {
        setError((e as Error).message || 'Could not update event date');
      }
    },
    [dispatch, widgetId, event, state.widgetBootstrapEventId, state.widgetBootstrapFirstOccurrenceIso],
  );

  useEffect(() => {
    if (state.step === 'tickets' && selectedTables.length > 0) {
      setReservationModalOpen(true);
    }
  }, [state.step, selectedTables.length]);

  useEffect(() => {
    if (!showTableModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || chooseForMeOpen || dateModalOpen) return;
      if (state.step === 'confirmation') {
        resetAfterPurchase();
        return;
      }
      if (inCheckout) {
        void handleCheckoutBack();
      } else {
        setReservationModalOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showTableModal, chooseForMeOpen, dateModalOpen, inCheckout, handleCheckoutBack, state.step, resetAfterPurchase]);

  useEffect(() => {
    if (!showTableModal) return;
    return beginModalBodyScrollLock();
  }, [showTableModal]);

  if (!event || !venue) return null;

  if (!feeTierId) {
    return (
      <div class="mw-tl-error">
        This event has no table ticket tier configured.
      </div>
    );
  }

  const venueName = venue.name || 'Venue';
  const showDatePicker = purchasableOccurrenceEntries.length > 1;
  const hideDateOnHeroDesktop = widget?.layout === 'desktop' && widget?.show_event_image;
  const currentOccIso = event ? resolveDisplayOccurrenceIso(event, selectedOccurrenceIso) : '';
  const holdCta = widget?.custom_cta_text?.trim() || 'Continue to checkout';

  const openReservation = () => setReservationModalOpen(true);

  return (
    <div
      class={[
        'mw-tl',
        showTableModal ?         'mw-tl--rsv-open' : '',
        inCheckout ? 'mw-tl--in-checkout' : '',
        state.step === 'confirmation' ? 'mw-tl--post-purchase' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {!inCheckout && state.step !== 'confirmation' && (
        <div class="mw-tl-topbar">
          <span class="mw-tl-venue-name">{venueName}</span>
        </div>
      )}

      {state.step === 'tickets' && !reservationModalOpen && (
        <div class="mw-tl-landing">
          {loading ? (
            <div class="mw-tl-loading" style={{ minHeight: 180 }}>
              <div class="mw-spinner" />
              <p style={{ marginTop: '0.75rem' }}>Loading table options…</p>
            </div>
          ) : (
            <>
              <h1 class="mw-tl-h1">Table reservations</h1>
              <p class="mw-tl-landing-lead">Choose your table, then check out in a few steps.</p>
              {showDatePicker && !hideDateOnHeroDesktop && (
                <button type="button" class="mw-tl-date-btn" onClick={() => setDateModalOpen(true)}>
                  {currentOccIso
                    ? `Event date: ${formatOccurrenceShort(currentOccIso, event.timezone)} — change`
                    : 'Select event date'}
                </button>
              )}
              {selectedTables.length > 0 && (
                <p class="mw-tl-landing-cart-hint">
                  {selectedTables.length} line{selectedTables.length === 1 ? '' : 's'} in your cart. Open to continue.
                </p>
              )}
              {error && (
                <div class="mw-tl-error" style={{ marginBottom: '0.75rem' }}>
                  {error}
                  <button type="button" class="mw-tl-cfm-close" onClick={() => setError(null)} style={{ float: 'right' }}>✕</button>
                </div>
              )}
              <button
                type="button"
                class="mw-tl-landing-cta"
                onClick={openReservation}
                disabled={continuing}
              >
                Reserve table
              </button>
            </>
          )}
        </div>
      )}

      {showTableModal && (
        <div class="mw-tl-rsv-backdrop" role="dialog" aria-modal="true" aria-labelledby="mw-tl-rsv-title">
          <div class="mw-tl-rsv-panel" ref={tlPanelRef} onClick={(e) => e.stopPropagation()}>
            {state.step === 'confirmation' ? (
              <>
                <div class="mw-tl-rsv-head mw-tl-rsv-head--tri">
                  <span class="mw-tl-rsv-edge" aria-hidden="true" />
                  <h2 id="mw-tl-rsv-title" class="mw-tl-rsv-title">
                    Success
                  </h2>
                  <button
                    type="button"
                    class="mw-tl-rsv-close"
                    onClick={resetAfterPurchase}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div class="mw-tl-rsv-body mw-tl-rsv-body--confirm">
                  <Confirmation variant="embed" onGetMoreTickets={resetAfterPurchase} />
                </div>
              </>
            ) : (
              <>
                <div class="mw-tl-rsv-head mw-tl-rsv-head--tri">
                  {inCheckout ? (
                    <button
                      type="button"
                      class="mw-tl-rsv-back"
                      onClick={() => void handleCheckoutBack()}
                      aria-label="Back"
                    >
                      ←
                    </button>
                  ) : (
                    <span class="mw-tl-rsv-edge" aria-hidden="true" />
                  )}
                  <h2 id="mw-tl-rsv-title" class="mw-tl-rsv-title">
                    {inCheckout
                      ? state.step === 'contact'
                        ? 'Your info'
                        : 'Payment'
                      : 'Table reservations'}
                  </h2>
                  <div class="mw-tl-rsv-head-right">
                    {!inCheckout && (
                      <button
                        type="button"
                        ref={tlCartBtnRef}
                        class={['mw-tl-rsv-cart-btn', cartPulse ? 'mw-tl-rsv-cart-btn--pulse' : ''].filter(Boolean).join(' ')}
                        onClick={scrollToCartSection}
                        aria-label={`Table cart, ${tableCartCount} line${tableCartCount === 1 ? '' : 's'}`}
                      >
                        <svg
                          class="mw-tl-rsv-cart-ico"
                          viewBox="0 0 24 24"
                          width="22"
                          height="22"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          overflow="visible"
                          aria-hidden="true"
                        >
                          <g transform="translate(0 0.5)">
                            <path d="M6 2 3.5 6.5V20A1.5 1.5 0 0 0 5 21.5h14a1.5 1.5 0 0 0 1.5-1.5V6.5L18 2" />
                            <path d="M3.5 6.5h17" />
                            <path d="M16 10a4 4 0 0 0-8 0" />
                          </g>
                        </svg>
                        {tableCartCount > 0 && (
                          <span class="mw-tl-rsv-cart-badge" aria-hidden="true">
                            {tableCartCount > 99 ? '99+' : tableCartCount}
                          </span>
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      class="mw-tl-rsv-close"
                      onClick={() =>
                        inCheckout ? void handleCheckoutBack() : setReservationModalOpen(false)
                      }
                      aria-label={inCheckout ? 'Back' : 'Close'}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {inCheckout ? (
              <div class="mw-tl-rsv-body mw-tl-rsv-body--checkout">
                {state.step === 'contact' ? (
                  <div class="mw-tl-embed-contact">
                    <ContactForm />
                  </div>
                ) : (
                  <Payment />
                )}
              </div>
            ) : (
              <>
            <div class="mw-tl-rsv-body">
        <div class="mw-tl-info">
          <span class="mw-tl-info-icon" aria-hidden>ℹ</span>
          <span>
            {omitTableTimeSlots
              ? 'Pick a table for the event, add it to your cart, then check out.'
              : 'Only event date time slots are shown. Add slots to your cart, then check out.'}
          </span>
        </div>

        {showDatePicker && !hideDateOnHeroDesktop && (
          <button type="button" class="mw-tl-date-btn" onClick={() => setDateModalOpen(true)}>
            {currentOccIso
              ? `Event date: ${formatOccurrenceShort(currentOccIso, event.timezone)} — change`
              : 'Select event date'}
          </button>
        )}

        {error && (
          <div class="mw-tl-error">
            {error}
            <button type="button" class="mw-tl-cfm-close" onClick={() => setError(null)} style={{ float: 'right' }}>✕</button>
          </div>
        )}

        {!omitTableTimeSlots && eventDays.length > 0 && selectedDay && (
          <>
            {eventDays.length > 1 && (
              <>
                <p class="mw-tl-subtitle">Event day</p>
                <div class="mw-tl-day-toggles" role="group" aria-label="Event day">
                  {eventDays.map((d) => (
                    <button
                      type="button"
                      key={d}
                      class={selectedDay === d ? 'mw-tl-toggle mw-tl-toggle--on' : 'mw-tl-toggle'}
                      onClick={() => setSelectedDay(d)}
                    >
                      {shortDayLabel(d)}
                    </button>
                  ))}
                </div>
              </>
            )}
            <p class="mw-tl-subhint">Times for {shortDayLabel(selectedDay)} only</p>
          </>
        )}

        {!omitTableTimeSlots && eventDays.length === 0 && (
          <p class="mw-tl-subhint mw-tl-subhint--muted">This event has no start date we can use for weekday filtering.</p>
        )}

        {!loading && (
          <div
            class="mw-tl-cfm-wrap"
            style={{
              opacity:
                continuing
                || !eventId
                || maxPartyForDay < 1
                || (omitTableTimeSlots
                  ? tablesWithSlots.length === 0
                  : chooseForMeSlotOptions.length === 0)
                  ? 0.5
                  : 1,
            }}
          >
            <div class="mw-tl-cfm-inner">
              <div class="mw-tl-cfm-icon" aria-hidden>✦</div>
              <h2 class="mw-tl-cfm-h2">Not sure where to sit?</h2>
              <p class="mw-tl-cfm-p">
                {omitTableTimeSlots
                  ? "Tell us your party size and we'll pick the best available table for the event."
                  : 'Let our curator pick the best available table for your party and time window.'}
              </p>
              <button
                type="button"
                class="mw-tl-cfm-cta"
                disabled={
                  continuing
                  || !eventId
                  || maxPartyForDay < 1
                  || (omitTableTimeSlots
                    ? tablesWithSlots.length === 0
                    : chooseForMeSlotOptions.length === 0)
                }
                onClick={() => setChooseForMeOpen(true)}
              >
                Choose for me
              </button>
            </div>
          </div>
        )}

        <div class="mw-tl-legend" aria-label="Status legend">
          {(
            [
              ['Available', 'avail'],
              ['Held', 'held'],
              ['Sold', 'sold'],
            ] as const
          ).map(([label, k]) => (
            <div class="mw-tl-legend-item" key={label}>
              <span class={`mw-tl-dot mw-tl-dot--${k}`} />
              {label}
            </div>
          ))}
        </div>

        {loading ? (
          <div class="mw-tl-loading">
            <div class="mw-spinner" />
            <p style={{ marginTop: '0.75rem' }}>Loading tables…</p>
          </div>
        ) : tablesWithSlots.length === 0 ? (
          <p class="mw-tl-empty-hint">
            {omitTableTimeSlots
              ? 'No tables are available for this event.'
              : eventDays.length === 0
                ? 'No times to show until the event has a valid start date.'
                : 'No tables have slots for this event day.'}
          </p>
        ) : (
          <div class="mw-tl-bento">
            {tablesWithSlots.map(({ table, slots }) => {
              const tid = String(table.id);
              const title = table.label || tid;
              return (
                <details key={tid} class="mw-tl-acc">
                  <summary>
                    <span>{title}</span>
                    <span class="mw-tl-seats">👥 {table.chairs ?? '—'} seats</span>
                  </summary>
                  {slots.map((slot, si) => {
                    if (!slot) {
                      const st = statusForTableSlot(tid, null, tableAvailability);
                      const disabled = st === 'sold' || st === 'held' || st === 'blocked';
                      const raw = resolvePricingForSlot(tablePricing, tid, null);
                      const price = tablePriceFromEntry(raw, table.chairs || 1);
                      const isSel = selectedKeys.has(tid);
                      const priceLabel = tableListPriceLabel(price, raw, null, feeCurrency);
                      const incLine = getFeePreviewInclusionsFromUnknownRow(raw);
                      return (
                        <div class="mw-tl-slot" key="whole">
                          <div class="mw-tl-slot-row1">
                            <span
                              class={
                                st === 'sold' ? 'mw-tl-pill mw-tl-pill-sold' : st === 'held' ? 'mw-tl-pill mw-tl-pill-held' : 'mw-tl-pill mw-tl-pill-avail'
                              }
                            >
                              {tableStatusPillText(st)}
                            </span>
                            {omitTableTimeSlots ? (
                              <span class="mw-tl-price" style={{ opacity: disabled ? 0.5 : 1 }}>{priceLabel}</span>
                            ) : null}
                          </div>
                          {!omitTableTimeSlots ? (
                          <div class="mw-tl-time-row" style={{ opacity: disabled ? 0.5 : 1 }}>
                            <span class="mw-tl-time-label">Open seating</span>
                            <span class="mw-tl-price">{priceLabel}</span>
                          </div>
                          ) : null}
                          {incLine ? <p class="mw-tl-fee-inclusions">{incLine}</p> : null}
                          <button
                            type="button"
                            class={isSel ? 'mw-tl-add mw-tl-add--in' : 'mw-tl-add'}
                            disabled={disabled}
                            onClick={() => makeToggleHandler(table, null)}
                          >
                            {isSel
                              ? `Remove · ${priceLabel}`
                              : `Add to cart · ${priceLabel}`}
                          </button>
                        </div>
                      );
                    }
                    const st = statusForTableSlot(tid, slot, tableAvailability);
                    const disabled = st === 'sold' || st === 'held' || st === 'blocked';
                    const key = tableSlotKey(tid, slot);
                    const isSel = selectedKeys.has(key);
                    const fromSlot = slot.slotPricingSource
                      ? tablePriceFromEntry(slot.slotPricingSource as unknown, table.chairs || 1)
                      : 0;
                    const raw = resolvePricingForSlot(tablePricing, tid, slot);
                    const price =
                      fromSlot > 0 ? fromSlot : tablePriceFromEntry(raw, table.chairs || 1);
                    const timeStr = slotLabel({ day: slot.day, start_time: slot.start_time, end_time: slot.end_time });
                    const priceLabel = tableListPriceLabel(price, raw, slot, feeCurrency);
                    const incLine =
                      getFeePreviewInclusionsFromUnknownRow(raw) ||
                      getFeePreviewInclusionsFromUnknownRow(slot.slotPricingSource);
                    return (
                      <div class="mw-tl-slot" key={key + si}>
                        <div class="mw-tl-slot-row1">
                          <span class={`mw-tl-pill ${st === 'sold' ? 'mw-tl-pill-sold' : st === 'held' ? 'mw-tl-pill-held' : 'mw-tl-pill-avail'}`}>
                            {tableStatusPillText(st)}
                          </span>
                        </div>
                        <div class="mw-tl-time-row" style={{ opacity: disabled ? 0.5 : 1 }}>
                          <span class="mw-tl-time-label">{timeStr}</span>
                          <span class="mw-tl-price">{priceLabel}</span>
                        </div>
                        {incLine ? <p class="mw-tl-fee-inclusions">{incLine}</p> : null}
                        <button
                          type="button"
                          class={isSel ? 'mw-tl-add mw-tl-add--in' : 'mw-tl-add'}
                          disabled={disabled}
                          onClick={() => makeToggleHandler(table, slot)}
                        >
                          {isSel
                            ? `Remove · ${priceLabel}`
                            : `Add to cart · ${priceLabel}`}
                        </button>
                      </div>
                    );
                  })}
                </details>
              );
            })}
          </div>
        )}

        {selectedTables.length > 0 && (
          <div class="mw-tl-cart-lines" id="mw-tl-cart-section" ref={tlCartSectionRef}>
            <p class="mw-tl-subtitle" style={{ marginTop: '0.75rem' }}>In cart ({selectedTables.length})</p>
            {selectedTables.map((s, i) => {
              const line = s.timeSlot ? slotLabel(s.timeSlot) : null;
              const pr = s.pricingRule;
              const pl =
                pr && pr.price != null && Number.isFinite(pr.price)
                  ? getTableRulePriceDisplay(pr, feeCurrency)
                  : formatCurrencyMajor(0, currency);
              const incLine = pr ? formatFeePreviewInclusions(pr.fee_preview) : '';
              return (
                <div key={i} class="mw-tl-total-row mw-tl-total-row--cartline">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <span>{line ? `${s.label} · ${line}` : s.label}</span>
                    {incLine ? <p class="mw-tl-fee-inclusions mw-tl-fee-inclusions--cart">{incLine}</p> : null}
                  </div>
                  <span class="mw-tl-line-price">{pl}</span>
                </div>
              );
            })}
            <div class="mw-tl-total-row mw-tl-total-sep">
              <span>Subtotal</span>
              <span>{formatCurrencyMajor(selectionSubtotal, currency)}</span>
            </div>
            {selectionSubtotal > 0 && (
              <div class="mw-tl-total-row mw-tl-fee-row">
                <span>Service fees</span>
                <span>{state.calculatingFees ? '...' : formatCurrency(fees, currency)}</span>
              </div>
            )}
            {selectionSubtotal > 0 && tax > 0 && (
              <div class="mw-tl-total-row mw-tl-fee-row">
                <span>Tax</span>
                <span>{state.calculatingFees ? '...' : formatCurrency(tax, currency)}</span>
              </div>
            )}
            <div class="mw-tl-total-row" style={{ fontSize: '1rem' }}>
              <span>Total</span>
              <span>{state.calculatingFees ? '...' : formatCurrency(grandTotal, currency)}</span>
            </div>
          </div>
        )}
            </div>

            <div class="mw-tl-sticky mw-tl-sticky--modal">
        <button
          type="button"
          class="mw-tl-btn-clear"
          onClick={() => {
            if (holdId && holdApiSource === 'event_tables' && eventId) {
              void deleteEventTablesHold(eventId, holdId);
            }
            dispatch({ type: 'CLEAR_HOLD' });
            dispatch({ type: 'CLEAR_SELECTION' });
          }}
        >
          Clear cart
        </button>
        <button
          type="button"
          class="mw-tl-btn-reserve"
          onClick={() => void handleContinue()}
          disabled={continuing || selectedTables.length === 0}
        >
          {continuing ? 'Reserving…' : holdCta}
        </button>
            </div>
              </>
            )}
              </>
            )}
          </div>
        </div>
      )}

      <ChooseForMeModal
        open={chooseForMeOpen}
        onClose={() => !continuing && setChooseForMeOpen(false)}
        maxParty={maxPartyForDay}
        slotOptions={chooseForMeSlotOptions}
        omitTimeSlots={omitTableTimeSlots}
        busy={continuing}
        onConfirm={handleChooseForMeConfirm}
        serviceFeeText={cfmFeeText}
        totalText={cfmTotalText}
        inclusionsSubtext={cfmInclusionsText}
        onPreviewChange={onChooseForMePreview}
      />

      {showDatePicker && (
        <EventDateSelectModalWidget
          open={dateModalOpen}
          onClose={() => setDateModalOpen(false)}
          occurrences={purchasableOccurrenceEntries}
          selectedIso={currentOccIso}
          onConfirm={onDateModalConfirm}
          timezone={event.timezone}
        />
      )}

      {cartFly && (
        <div
          key={cartFly.id}
          class="mw-tl-cart-fly-dot"
          style={
            {
              left: cartFly.sx,
              top: cartFly.sy,
              '--mw-tl-fly-dx': `${cartFly.dx}px`,
              '--mw-tl-fly-dy': `${cartFly.dy}px`,
            } as Record<string, string | number>
          }
        />
      )}
    </div>
  );
};
